/* eslint-disable max-len */
import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';

import loadIcons from '../requests/loadIcons';
import loadNavigation from '../requests/loadNavigation';
import { NavigationContext } from '../context/NavigationContext';
import NavigationReducer, { initState } from '../context/NavigationReducer';
import NavigationContainer from './NavigationContainer';

import useWindowSize from '../util/useWindowSize';
import validateParams from '../util/validateParams';
import ErrorBoundary from './ErrorBoundary';
import loadAudienceNavigation from '../requests/loadAudienceNavigation';

const STRUCTURE_DESKTOP = 'desktop';
const STRUCTURE_MOBILE = 'mobile';

const App = ({
    otherProps: {
        navigationApiUrl,
        navigationAppUrl,
        blockId,
        blockName,
        dynamicContent = {},
        isPhone = false,
        instance: {
            id: instanceId,
            navigationConfig = {},
            darkDistrictConfig = {},
            showFirstNamesOnly = false,
        } = {},
        mobileBreakPoint = 999,
        navigationAudienceId,
        navigationId,
        memberData: { // memberData passed from MA
            authToken,
            countryCode,
            continentCode,
            regionCode,
        } = {},
        paths,
        pageId,
        pageType,
        statePage,
        onHelmet,
        onLink,
        onFlirt4FreeLink,
        onSearch,
        onSearchRedirect,
        onSetSearchContext,
        onSticky,
        preStructure = null,
        query = {},
        referrerHeaders = {},
        result = {},
        routesMap = {},
        theme = {},
        user = {},
        config,
        navAlignment: initialAlignment = 'top',
    } = {},
}) => {
    const isPreview = Boolean(preStructure);

    validateParams([
        { name: 'navigationApiUrl', type: 'string', value: navigationApiUrl },
        { name: 'navigationAppUrl', type: 'string', value: navigationAppUrl },
        { name: 'navigationId', type: 'number', value: navigationId, optional: Boolean(navigationAudienceId) || isPreview },
        { name: 'navigationAudienceId', type: 'number', value: navigationAudienceId, optional: Boolean(navigationId) || isPreview },
        { name: 'onHelmet', type: 'function', value: onHelmet },
        { name: 'onLink', type: 'function', value: onLink },
        { name: 'onSearch', type: 'function', value: onSearch }, // deprecated in SearchBoxV2
        { name: 'onSearchRedirect', type: 'function', value: onSearchRedirect },
        { name: 'onSticky', type: 'function', value: onSticky },
        { name: 'referrerHeaders', type: 'object', value: referrerHeaders },
    ]);

    const [structure, setStructure] = useState(preStructure);
    const [navAlignment, setNavAlignment] = useState(initialAlignment);
    const [state, dispatch] = useReducer(NavigationReducer, initState);
    const [iconData, setIconData] = useState({});

    const { width } = useWindowSize({ wait: 250 });
    const collapseToMobile = width && width < mobileBreakPoint;
    const structureType = isPhone || (!isPhone && collapseToMobile) ? STRUCTURE_MOBILE : STRUCTURE_DESKTOP;

    const menuActions = { state, dispatch };

    const providerValues = {
        ...menuActions,
        dynamicContent,
        instanceId,
        blockId,
        blockName,
        referrerHeaders,
        routesMap,
        navigationApiUrl,
        navigationConfig,
        darkDistrictConfig,
        pageType,
        pageId,
        statePage,
        onHelmet,
        onLink,
        onFlirt4FreeLink,
        onSearch,
        onSearchRedirect,
        onSetSearchContext,
        query,
        paths,
        result,
        showFirstNamesOnly,
        user,
        iconData,
        navAlignment,
        setNavAlignment,
    };

    const errorBoundaryContext = {
        navigationId,
        navigationAppUrl,
    };

    const loadIcon = async () => {
        const { iconPackId } = config;
        if (iconPackId) {
            const iconResponse = await loadIcons({ navigationApiUrl, iconPackId });
            if (iconResponse?.id) {
                setIconData(iconResponse);
            }
        }
    };

    useEffect(() => {
        if (preStructure && preStructure !== structure) {
            setStructure(preStructure);
            loadIcon();
        }
    }, [preStructure]);

    useEffect(() => {
        if (!structure && !preStructure) {
            const loadFunction = navigationAudienceId ? loadAudienceNavigation : loadNavigation;
            const loadConfig = navigationAudienceId
                ? { navigationApiUrl, navigationAudienceId, authToken, countryCode, continentCode, regionCode }
                : { navigationApiUrl, navigationId };

            const load = async () => {
                const navResponse = await loadFunction(loadConfig)
                    .then(resp => {
                        const { structure: blob = '', config: iconsConfig } = resp || {};
                        return {
                            blob: JSON.parse(blob),
                            icons: JSON.parse(iconsConfig || '{}'),
                        };
                    })
                    .catch(err => {
                        console.error('Failed to parse config', err);
                        return null;
                    });

                const { blob, icons: { iconPackId } = {} } = navResponse || {};

                if (blob) {
                    setStructure(blob);
                }
                if (iconPackId) {
                    const iconResponse = await loadIcons({ navigationApiUrl, iconPackId });
                    if (iconResponse?.id) {
                        setIconData(iconResponse);
                    }
                }
            };
            load();
        }
    }, []);

    useEffect(() => {
        let timeout;
        if (structure) {
            // timeout from NavComponent useIsPPPBarOffFold() hook
            timeout = setTimeout(() => {
                if (isPreview) {
                    loadIcon();
                }
                const element = document.querySelector('.nav-component') || {};
                const { clientHeight = 0, style: { position } = {} } = element;
                const isSticky = position === 'fixed';
                if (isSticky && onSticky) {
                    onSticky({ navHeight: clientHeight });
                }
            }, 500);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [structure]);

    return structure ? (
        <ErrorBoundary {...errorBoundaryContext}>
            <NavigationContext.Provider value={providerValues}>
                <ThemeProvider theme={theme}>
                    <NavigationContainer
                        className={structureType}
                        menuActions={menuActions}
                        isPreview={isPreview}
                        structure={structure}
                        structureType={structureType}
                    />
                </ThemeProvider>
            </NavigationContext.Provider>
        </ErrorBoundary>
    ) : null;
};

App.propTypes = {
    otherProps: PropTypes.shape({
        navigationApiUrl: PropTypes.string.isRequired,
        navigationAppUrl: PropTypes.string.isRequired,
        blockId: PropTypes.number,
        blockName: PropTypes.string,
        dynamicContent: PropTypes.object,
        isPhone: PropTypes.bool,
        instance: PropTypes.shape({
            navigationConfig: PropTypes.object,
            darkDistrictConfig: PropTypes.object,
            showFirstNamesOnly: PropTypes.bool,
            id: PropTypes.number,
        }),
        mobileBreakPoint: PropTypes.number,
        navigationId: PropTypes.number,
        navigationAudienceId: PropTypes.number,
        pageType: PropTypes.string,
        statePage: PropTypes.string,
        pageId: PropTypes.number,
        paths: PropTypes.object,
        onHelmet: PropTypes.func.isRequired,
        onLink: PropTypes.func.isRequired,
        onSearch: PropTypes.func.isRequired,
        onSearchRedirect: PropTypes.func.isRequired,
        onFlirt4FreeLink: PropTypes.func.isRequired,
        onSticky: PropTypes.func.isRequired,
        query: PropTypes.object,
        referrerHeaders: PropTypes.object,
        routesMap: PropTypes.object,
        theme: PropTypes.object,
        user: PropTypes.shape({
            isNSF: PropTypes.bool,
            isExpired: PropTypes.bool,
            boxToken: PropTypes.string,
            memberUuid: PropTypes.string,
        }),
    }).isRequired,
};

export default App;
